import { EntityModules, UserRole } from 'core/constants';
import { UserLicensesResponse } from 'utils/api/modules/user/user.types';

// These keys must match the Knock dashboard exactly — edit with care.
export const WorkflowKey = {
  taskAssigned: 'task-assigned',
  taskCommentMention: 'task-comment-mention',
  dueToday: 'due-today',
  eventOwnerAssigned: 'event-owner-assigned',
  eventStageChanged: 'event-stage-changed',
  eventCollaborated: 'event-collaborated',
  eventComment: 'event-comment',
  eventCommentMention: 'event-comment-mention',
  eventReported: 'event-reported',
  eventReportedCollaboratorOnly: 'event-reported-collaborator-only',
  eventCollaboratedFreshWindow: 'event-collaborated-fresh-window',
  auditSubmitted: 'audit-submitted',
  auditCommentMention: 'audit-comment-mention',
  auditScheduleAssigned: 'audit-schedule-assigned',
  acknowledgementAssigned: 'acknowledgement-assigned',
  siteManagerAssigned: 'site-manager-assigned',
  siteCommentMention: 'site-comment-mention',
  assetCommentMention: 'asset-comment-mention',
  documentCommentMention: 'document-comment-mention',
  riskCommentMention: 'risk-comment-mention',
  riskActionAssigned: 'risk-action-assigned',
} as const;

// Knock category keys. Categories group preferences and are what broadcasts target, so a
// category-typed toggle writes to preferenceSet.categories rather than .workflows.
export const CategoryKey = {
  productUpdates: 'product_updates',
} as const;

export interface NotificationWorkflow {
  key: string;
  label: string;
  description?: string;
  // 'category' rows write to a Knock preference category (broadcasts); defaults to 'workflow'.
  kind?: 'workflow' | 'category';
  // Channels this row is togglable on. Omitted = all channels. Email-only rows set ['email'].
  channels?: string[];
}

export interface NotificationCategory {
  key: string;
  label: string;
  description: string;
  workflows: NotificationWorkflow[];
}

export interface ChannelType {
  key: string;
  label: string;
}

export interface WorkflowItem {
  key: string;
  label: string;
  description?: string;
  channels?: string[];
}

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    key: 'actions',
    label: 'Actions',
    description: 'Action assignments, comments, mentions and reminders',
    workflows: [
      {
        key: WorkflowKey.taskAssigned,
        label: 'Task assigned to me',
        description: "When you're assigned a new task",
      },
      {
        key: WorkflowKey.taskCommentMention,
        label: 'Mentioned in task comment',
        description: 'When someone @mentions you in a comment on a task',
      },
      {
        key: WorkflowKey.dueToday,
        label: 'Due today',
        description: 'When an action assigned to you is due today',
      },
    ],
  },
  {
    key: 'events',
    label: 'Events',
    description: 'Event assignments, stage changes, comments and submissions',
    workflows: [
      {
        key: WorkflowKey.eventOwnerAssigned,
        label: 'Event assigned to me',
        description: "When you're assigned as the owner of an event",
      },
      {
        key: WorkflowKey.eventStageChanged,
        label: 'Event stage changed',
        description: "When the stage is updated on an event you're collaborating on",
      },
      {
        key: WorkflowKey.eventCollaborated,
        label: 'Added as collaborator',
        description: "When you're added as a collaborator on an event",
      },
      {
        key: WorkflowKey.eventComment,
        label: 'New comment on event',
        description: "When a new comment is posted on an event you're collaborating on",
      },
      {
        key: WorkflowKey.eventCommentMention,
        label: 'Mentioned in event comment',
        description: 'When someone @mentions you in a comment on an event',
      },
      {
        key: WorkflowKey.eventReported,
        label: 'New event submitted',
        description: 'When any event is submitted in your organisation',
      },
      {
        key: WorkflowKey.eventReportedCollaboratorOnly,
        label: 'New events I collaborate on',
        description: "When a new event is submitted and you're added as a collaborator on it",
      },
    ],
  },
  {
    key: 'audits',
    label: 'Audits',
    description: 'Audit submissions and schedules',
    workflows: [
      {
        key: WorkflowKey.auditSubmitted,
        label: 'Audit submitted',
        description: 'When an audit is submitted by anyone in your organisation',
      },
      {
        key: WorkflowKey.auditScheduleAssigned,
        label: 'Audit schedule assigned to me',
        description: "When you're assigned an audit schedule to carry out",
      },
      {
        key: WorkflowKey.auditCommentMention,
        label: 'Mentioned in audit comment',
        description: 'When someone @mentions you in a comment on an audit',
      },
    ],
  },
  {
    key: 'documents',
    label: 'Documents',
    description: 'Document acknowledgements and mentions',
    workflows: [
      {
        key: WorkflowKey.acknowledgementAssigned,
        label: 'Acknowledgement assigned to me',
        description: "When you're assigned a document acknowledgement",
      },
      {
        key: WorkflowKey.documentCommentMention,
        label: 'Mentioned in document comment',
        description: 'When someone @mentions you in a comment on a document',
      },
    ],
  },
  {
    key: 'sites',
    label: 'Sites',
    description: 'Site manager assignments, comments and mentions',
    workflows: [
      {
        key: WorkflowKey.siteManagerAssigned,
        label: 'Site assigned to me',
        description: "When you're assigned as a site manager",
      },
      {
        key: WorkflowKey.siteCommentMention,
        label: 'Mentioned in site comment',
        description: 'When someone @mentions you in a comment on a site',
      },
    ],
  },
  {
    key: 'assets',
    label: 'Assets',
    description: 'Asset comments and mentions',
    workflows: [
      {
        key: WorkflowKey.assetCommentMention,
        label: 'Mentioned in asset comment',
        description: 'When someone @mentions you in a comment on an asset',
      },
    ],
  },
  {
    key: 'risks',
    label: 'Risks',
    description: 'Risk comments, mentions and actions',
    workflows: [
      {
        key: WorkflowKey.riskActionAssigned,
        label: 'Risk action assigned to me',
        description: "When you're assigned a risk action",
      },
      {
        key: WorkflowKey.riskCommentMention,
        label: 'Mentioned in risk comment',
        description: 'When someone @mentions you in a comment on a risk',
      },
    ],
  },
  {
    key: 'product',
    label: 'Product updates',
    description: 'Product release announcements',
    workflows: [
      {
        key: CategoryKey.productUpdates,
        kind: 'category',
        label: 'Product release emails',
        description: 'A monthly summary of new features and improvements, with occasional updates for bigger releases.',
        channels: ['email'],
      },
    ],
  },
];

export const CHANNEL_TYPES: ChannelType[] = [
  { key: 'email', label: 'Email' },
  { key: 'in_app_feed', label: 'Inbox' },
];

// The channels a row exposes a toggle for. Omitted = all CHANNEL_TYPES.
export const getItemChannels = (item: { channels?: string[] }): ChannelType[] =>
  item.channels ? CHANNEL_TYPES.filter((c) => item.channels!.includes(c.key)) : CHANNEL_TYPES;

// Must mirror the workflow's default preference configured in the Knock dashboard (per channel).
// Every OFF row is OFF on both email and inbox, so a single per-workflow value covers both channels.
export const WORKFLOW_DEFAULTS: Record<string, boolean> = {
  [WorkflowKey.eventStageChanged]: false,
  [WorkflowKey.eventComment]: false,
  [WorkflowKey.eventReported]: false,
  [WorkflowKey.auditSubmitted]: false,
  [WorkflowKey.eventCollaboratedFreshWindow]: false,
};

export const getWorkflowDefault = (workflowKey: string, role?: UserRole): boolean => {
  // Product release emails default ON for account owners only; Managers and Users opt in.
  // The per-user value is seeded in Knock at sync time; this mirror covers the not-yet-seeded case.
  if (workflowKey === CategoryKey.productUpdates) return role === UserRole.AccountManager;
  return WORKFLOW_DEFAULTS[workflowKey] ?? true;
};

// Hidden workflow keys whose pref is computed from visible toggles, not user-set.
export const DERIVED_WORKFLOW_KEYS = [WorkflowKey.eventCollaboratedFreshWindow] as const;

// Workflow pairs that are mutually exclusive in the UI. Turning one ON forces the other OFF.
export const XOR_PAIRS: Record<string, string> = {
  [WorkflowKey.eventReported]: WorkflowKey.eventReportedCollaboratorOnly,
  [WorkflowKey.eventReportedCollaboratorOnly]: WorkflowKey.eventReported,
};

// Users can't be collaborators and don't receive org-wide broadcasts.
const USER_HIDDEN_EVENT_KEYS = new Set<string>([
  WorkflowKey.eventStageChanged,
  WorkflowKey.eventCollaborated,
  WorkflowKey.eventComment,
  WorkflowKey.eventReported,
  WorkflowKey.eventReportedCollaboratorOnly,
]);

const toItem = (w: NotificationWorkflow): WorkflowItem => ({
  key: w.key,
  label: w.label,
  description: w.description,
  channels: w.channels,
});

// Manager doesn't receive the broadcast (event-reported), so we hide it and re-wire the
// "New event submitted" label onto event-reported-collaborator-only — the workflow they actually receive.
export const getEventWorkflowItems = (role: UserRole | undefined): WorkflowItem[] => {
  const eventsCategory = NOTIFICATION_CATEGORIES.find((c) => c.key === 'events');
  if (!eventsCategory) return [];

  if (role === UserRole.User) {
    return eventsCategory.workflows.filter((w) => !USER_HIDDEN_EVENT_KEYS.has(w.key)).map(toItem);
  }

  if (role === UserRole.Manager) {
    return eventsCategory.workflows
      .filter((w) => w.key !== WorkflowKey.eventReported)
      .map((w) =>
        w.key === WorkflowKey.eventReportedCollaboratorOnly
          ? {
              ...toItem(w),
              label: 'New event submitted',
              description: "When a new event you're added to as a collaborator is submitted",
            }
          : toItem(w)
      );
  }

  return eventsCategory.workflows.map(toItem);
};

export const getVisibleCategories = (
  licenses: UserLicensesResponse,
  activeEntityModules: EntityModules[]
): NotificationCategory[] =>
  NOTIFICATION_CATEGORIES.filter((category) => {
    switch (category.key) {
      case 'actions':
        return true;
      case 'events':
        return licenses.events;
      case 'audits':
        return licenses.workflows;
      case 'sites':
        return activeEntityModules.includes(EntityModules.Sites);
      case 'assets':
        return activeEntityModules.includes(EntityModules.Assets);
      case 'documents':
        return activeEntityModules.includes(EntityModules.Documents);
      case 'risks':
        return licenses.risks;
      case 'product':
        // Product release emails are available to every active user, regardless of role or licence.
        return true;
      default:
        return true;
    }
  });

const GATED_WORKFLOWS = new Set<string>([WorkflowKey.acknowledgementAssigned, WorkflowKey.riskActionAssigned]);

export const getCategoryWorkflowItems = (
  category: NotificationCategory,
  role: UserRole | undefined,
  enabledWorkflows: Record<string, boolean> = {}
): WorkflowItem[] => {
  if (category.key === 'events') return getEventWorkflowItems(role);

  return category.workflows
    .filter((w) => !(category.key === 'audits' && role === UserRole.User && w.key === WorkflowKey.auditSubmitted))
    .filter((w) => !GATED_WORKFLOWS.has(w.key) || enabledWorkflows[w.key])
    .map(toItem);
};
